import { useChatSurfacePrefs } from '@/lib/chatSurfacePrefs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip, ScrollShadow } from '@heroui/react';
import { useAuthStore } from '@/stores/authStore';
import { useShallow } from 'zustand/react/shallow';
import { useDataStore } from '@/stores/dataStore';
import { pickKeys } from '@/lib/pickKeys';
import { clampRoleColor } from '@/lib/roleColor';
import { useGuildRoster } from '@/lib/useGuildRoster';
import { useProgressiveList } from '@/lib/useProgressiveList';
import { LoadError } from '@/components/ui/LoadError';
import { useTheme } from '@/hooks/useTheme';
import { buildRoster } from '@/lib/guildRoster';
import { PlusIcon, VolumeIcon, UsersIcon } from '@/components/icons';
import { HeroFadeIn } from '@/heroui/motion/HeroFadeIn';
import { HeroMemberRow } from './HeroMemberRow';
import { platformLinkTarget } from '@/lib/platformEmbed';

// Zustand selectors are consumed through useSyncExternalStore and must return
// the same reference while the store is unchanged. A literal `[]` fallback in
// the selector creates a fresh snapshot on every read and sends React into an
// update loop whenever a guild has no live match (the ordinary case).
const EMPTY_MATCH_ACTIVITIES: never[] = [];

/**
 * Right-side roster for the HeroUI shell. Fetches the active guild's members
 * and roles, groups them with the shared {@link buildRoster} helper, and
 * renders pure HeroUI rows (HeroMemberRow). Mirrors the Aether MemberPanel's
 * data flow but with HeroUI surface/typography and no resize handle.
 */
export function HeroMemberPanel({
  onCreateGroup,
  width,
  floating = false,
}: {
  onCreateGroup?: () => void;
  /** Controlled width (px) from HeroApp's resizable layout. */
  width?: number;
  /** Float as a rounded card (desktop inline). In the mobile drawer
   *  this is false so the panel fills the sheet instead of floating inside it. */
  floating?: boolean;
}) {
  const { t } = useTranslation();
  const surfaces = useChatSurfacePrefs();
  const guildId = useDataStore((s) => s.currentGuildId);
  const guild = useDataStore((s) => (guildId ? s.guilds[guildId] : undefined));
  const matchActivities = useDataStore((s) =>
    guildId ? (s.matchActivitiesByGuild[guildId] ?? EMPTY_MATCH_ACTIVITIES) : EMPTY_MATCH_ACTIVITIES,
  );
  const selectChannel = useDataStore((s) => s.selectChannel);
  const myId = useAuthStore((s) => s.user?.id ?? null);
  // Shared, version-aware roster fetch (deduped across the member panel,
  // channel list, user card, etc.).
  const { members, rolesById, isLoading, isError, refetch } = useGuildRoster(guildId);
  // Presence of THIS guild's members and voice of ITS channels only: the
  // whole-store maps re-rendered the roster on every status change anywhere.
  const memberIds = useMemo(() => members.map((member) => member.userId), [members]);
  const voiceChannelIds = useMemo(
    () => (guild?.channels ?? []).filter((channel) => channel.type === 'VOICE').map((channel) => channel.id),
    [guild?.channels],
  );
  const presence = useDataStore(useShallow((s) => pickKeys(s.presence, memberIds)));
  const voiceByChannel = useDataStore(useShallow((s) => pickKeys(s.voiceByChannel, voiceChannelIds)));
  const theme = useTheme();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (matchActivities.length === 0) return;
    const timer = window.setInterval(() => setNow(Date.now()), 10_000);
    return () => window.clearInterval(timer);
  }, [matchActivities.length]);

  const roster = useMemo(
    () => buildRoster(members, rolesById, presence, myId),
    [members, rolesById, presence, myId],
  );
  // Online groups first, then offline — built as they come near the viewport
  // (a 1500-member server froze the page building every row at once).
  const onlineByBucket = useMemo(
    () => roster.buckets.map((bucket) => bucket.entries.filter((entry) => entry.status !== 'OFFLINE')),
    [roster],
  );
  const totalRows = onlineByBucket.reduce((sum, online) => sum + online.length, 0) + roster.offline.length;
  const { limit, sentinelRef } = useProgressiveList(totalRows, { initial: 80, step: 200, resetKey: guildId });
  let budget = limit;

  const liveUserIds = useMemo(
    () =>
      new Set(
        matchActivities.flatMap((match) =>
          match.voiceChannels.flatMap((room) => room.userIds),
        ),
      ),
    [matchActivities],
  );
  const voiceActivity = useMemo(() => {
    if (!guild) return [];
    const matchChannelIds = new Set(
      matchActivities.flatMap((match) => match.voiceChannels.map((room) => room.channelId)),
    );
    return guild.channels
      .filter(
        (channel) =>
          channel.type === 'VOICE' &&
          !matchChannelIds.has(channel.id) &&
          (voiceByChannel[channel.id]?.length ?? 0) > 0,
      )
      .map((channel) => ({
        channelId: channel.id,
        name: channel.name || t('members.unnamedVoice'),
        userIds: (voiceByChannel[channel.id] ?? []).map((user) => user.userId),
      }));
  }, [guild, voiceByChannel, matchActivities, t]);
  const voiceLabelByUser = useMemo(() => {
    const labels = new Map<string, string>();
    for (const room of voiceActivity) {
      for (const userId of room.userIds) labels.set(userId, room.name);
    }
    return labels;
  }, [voiceActivity]);

  const rowActivity = (userId: string) => {
    if (liveUserIds.has(userId)) {
      const match = matchActivities.find((item) =>
        item.voiceChannels.some((room) => room.userIds.includes(userId)),
      );
      return {
        activityLabel: t('members.playingMatch', { map: match?.map || t('members.match') }),
        activityTone: 'live' as const,
      };
    }
    const room = voiceLabelByUser.get(userId);
    return room
      ? { activityLabel: t('members.inVoice', { channel: room }), activityTone: 'voice' as const }
      : {};
  };

  if (!guildId || !guild) return null;

  return (
    <aside
      data-wallpaper={surfaces.members}
      className={
        'members-surface ' + (floating
          ? 'studio-roster flex shrink-0 flex-col self-stretch overflow-hidden bg-surface'
          : 'flex h-full shrink-0 flex-col bg-background')
      }
      style={width ? { width } : undefined}
    >
      <header className="flex h-16 shrink-0 items-center justify-between px-4">
        <span className="text-sm font-semibold text-foreground">
          {t('members.heading')}{' '}
          {/* Label is "Участники" (all members) → show the TOTAL, not just
              online; the online/offline split is broken out in the list below. */}
          <span className="tabular-nums font-normal text-muted">
            — {roster.totalOnline + roster.totalOffline}
          </span>
        </span>
        {onCreateGroup && (
          <Tooltip delay={0}>
              <Button aria-label={t('members.createGroup')} isIconOnly size="sm" variant="ghost" onPress={onCreateGroup}>
                <PlusIcon className="size-4" />
              </Button>
            <Tooltip.Content>{t('members.createGroup')}</Tooltip.Content>
          </Tooltip>
        )}
      </header>

      <ScrollShadow hideScrollBar className="min-h-0 flex-1 p-2">
        <HeroFadeIn key={guildId} y={6} duration={0.3}>
        {isError ? (
          // Previously a failed roster fetch rendered the static grey bars
          // below FOREVER — indistinguishable from a broken layout.
          <LoadError error={null} onRetry={refetch} fallback={t('ui.memberPanel.loadFailed')} />
        ) : isLoading || members.length === 0 ? (
          <div className="space-y-1.5">
            {[0, 1, 2, 3].map((i) => (
              // Pulse so it reads as "loading", not as a rendering bug.
              <div key={i} className="flex animate-pulse items-center gap-2 px-2 py-1">
                <div className="size-8 shrink-0 rounded-full bg-default/60" />
                <div
                  className="h-3 flex-1 rounded bg-default/60"
                  style={{ maxWidth: `${60 - i * 10}%` }}
                />
              </div>
            ))}
          </div>
        ) : (
          <>
            {(matchActivities.length > 0 || voiceActivity.length > 0) && (
              <section className="mb-4 space-y-2">
                {matchActivities.map((match) => {
                  const participantCount = new Set(
                    match.voiceChannels.flatMap((room) => room.userIds),
                  ).size;
                  const targetChannel = match.voiceChannels[0]?.channelId;
                  return (
                    <div
                      key={match.matchRef}
                      onClick={() => targetChannel && selectChannel(targetChannel)}
                      onKeyDown={(event) => {
                        if ((event.key === 'Enter' || event.key === ' ') && targetChannel) {
                          selectChannel(targetChannel);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      className="w-full cursor-pointer rounded-xl border border-danger/25 bg-danger/10 px-3 py-2.5 text-left transition-colors hover:bg-danger/15"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-danger">
                          <span className="size-1.5 animate-pulse rounded-full bg-danger" /> LIVE
                        </span>
                        {match.score && (
                          <span className="font-semibold tabular-nums text-foreground">
                            {match.score.ct} : {match.score.t}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 truncate text-sm font-semibold text-foreground">
                        {match.homeTeam && match.awayTeam
                          ? `${match.homeTeam} — ${match.awayTeam}`
                          : match.map || t('members.match')}
                      </div>
                      {(match.homeTeam || match.awayTeam) && match.map && (
                        <div className="mt-0.5 truncate text-xs text-muted">
                          {[match.map, match.stage].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      <div className="mt-1 flex items-center gap-1.5 text-xs text-muted">
                        <UsersIcon className="size-3.5" />
                        {t('members.playersInVoice', { count: participantCount })}
                        {now - Date.parse(match.updatedAt) > 45_000 && (
                          <span className="ml-auto text-warning">{t('members.signalDelayed')}</span>
                        )}
                      </div>
                      {(match.platformUrl || match.reconUrl) && (
                        <div className="mt-3 flex gap-2">
                          {match.platformUrl && (
                            <a
                              href={match.platformUrl}
                              target={platformLinkTarget(match.platformUrl)}
                              rel="noopener noreferrer"
                              onClick={(event) => event.stopPropagation()}
                              className="text-xs font-medium text-accent hover:underline"
                            >
                              {t('members.openPlatform')}
                            </a>
                          )}
                          {match.reconUrl && (
                            <a
                              href={match.reconUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(event) => event.stopPropagation()}
                              className="text-xs font-medium text-accent hover:underline"
                            >
                              Recon
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {voiceActivity.length > 0 && (
                  <div className="px-2 pt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">
                    {t('members.voiceNow')}
                  </div>
                )}
                {voiceActivity.map((room) => (
                  <button
                    key={room.channelId}
                    type="button"
                    onClick={() => selectChannel(room.channelId)}
                    className="flex w-full items-center gap-2.5 rounded-xl bg-default/35 px-3 py-2 text-left transition-colors hover:bg-default/60"
                  >
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-success/15 text-success">
                      <VolumeIcon className="size-4" />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{room.name}</span>
                    <span className="rounded-full bg-success/15 px-2 py-0.5 text-xs font-semibold tabular-nums text-success">
                      {room.userIds.length}
                    </span>
                  </button>
                ))}
              </section>
            )}
            {roster.buckets.map((bucket, idx) => {
              const online = onlineByBucket[idx]!;
              const shown = online.slice(0, Math.max(0, budget));
              budget -= online.length;
              if (shown.length === 0) return null;
              const roleColor = bucket.role
                ? clampRoleColor(`#${bucket.role.color.toString(16).padStart(6, '0')}`, theme)
                : null;
              return (
                <section key={bucket.role?.id ?? `default-${idx}`} className="mb-3">
                  {bucket.role && (
                    <div
                      className="chat-member-group mb-1 flex items-center gap-1.5 px-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted"
                      style={roleColor ? { color: roleColor } : undefined}
                    >
                      <span className="size-1.5 shrink-0 rounded-full bg-current" />
                      <span>{bucket.role.name}</span>
                      <span className="tabular-nums">— {online.length}</span>
                    </div>
                  )}
                  <ul className="space-y-0.5">
                    {shown.map(({ member, status }) => (
                      <li key={member.userId}>
                        <HeroMemberRow
                          member={member}
                          guildId={guildId}
                          ownerId={guild.ownerId}
                          status={status}
                          {...rowActivity(member.userId)}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}

            {roster.totalOffline > 0 && budget > 0 && (
              <section className="mt-6">
                <div className="mb-1 flex items-center gap-1.5 px-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">
                  <span>{t('members.offline')}</span>
                  <span className="tabular-nums">— {roster.totalOffline}</span>
                </div>
                <ul className="space-y-0.5">
                  {roster.offline.slice(0, budget).map(({ member, status }) => (
                    <li key={member.userId}>
                      <HeroMemberRow
                        member={member}
                        guildId={guildId}
                        ownerId={guild.ownerId}
                        status={status}
                        {...rowActivity(member.userId)}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {limit < totalRows && <div ref={sentinelRef} aria-hidden className="h-px" />}
          </>
        )}
        </HeroFadeIn>
      </ScrollShadow>
    </aside>
  );
}
